import { Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, finalize } from 'rxjs';
import { ButtonModule } from '@openng/optimus-ui/button';
import { TagModule } from '@openng/optimus-ui/tag';
import {
  DSM_RECIPIENT_FAILURE_STATUSES,
  DSM_SIGNER_ROLE_LABELS,
  DsmAddendumEnvelope,
  DsmEnvelopeStatus,
  DsmRecipientStatus,
  DsmSignOffSigner,
  DsmSignOffTagSeverity,
  DsmSignerEntityType,
  DsmSigningTypeName
} from '../../../models/dsm-addendum-sign-off.models';
import { DsmAddendumSignOffService } from '../../../services/dsm-addendum-sign-off.service';
import { DsmApsSignOffContextService } from '../../../services/dsm-aps-sign-off-context.service';

function signerInitials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '—';
  const first = parts[0][0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

type SignerStatusTag = { label: string; severity: DsmSignOffTagSeverity };

type SignerRow = {
  signer: DsmSignOffSigner;
  initials: string;
  roleLabel: string;
  statusTag: SignerStatusTag | null;
  canGetSignature: boolean;
  canSendForSignature: boolean;
  canResend: boolean;
  signedDate: string | null;
  isPending: boolean;
};

@Component({
  selector: 'dsm-aps-sign-off-signers-list',
  standalone: true,
  imports: [DatePipe, ButtonModule, TagModule],
  templateUrl: './dsm-aps-sign-off-signers-list.component.html'
})
export class DsmApsSignOffSignersListComponent {
  readonly #signOffService = inject(DsmAddendumSignOffService);
  readonly #signOffContext = inject(DsmApsSignOffContextService);
  readonly #destroyRef = inject(DestroyRef);

  readonly envelope = input.required<DsmAddendumEnvelope>();
  readonly #pendingSignerId = signal<string | null>(null);
  readonly isBuilderRequestPending = signal(false);

  readonly rows = computed<SignerRow[]>(() => {
    const envelope = this.envelope();
    const signers = [...envelope.signers].sort((a, b) => a.sequence - b.sequence);
    const isInPerson =
      envelope.signingType === DsmSigningTypeName.InPerson ||
      envelope.signingType === DsmSigningTypeName.DocuSignInPerson;
    const isExpired = envelope.status === DsmEnvelopeStatus.Expired;
    const isClosed = envelope.status === DsmEnvelopeStatus.Voided || envelope.status === DsmEnvelopeStatus.Declined;
    const pendingSignerId = this.#pendingSignerId();

    const outstandingRoles = signers
      .filter(
        signer =>
          signer.entityType.toLowerCase() !== DsmSignerEntityType.Executive &&
          signer.status !== DsmRecipientStatus.Complete
      )
      .map(signer => DSM_SIGNER_ROLE_LABELS[signer.entityType.toLowerCase()] ?? signer.entityType);

    return signers.map(signer => {
      const entityType = signer.entityType.toLowerCase();
      const isBuilder = entityType === DsmSignerEntityType.Executive;
      const isSigned = signer.status === DsmRecipientStatus.Complete;
      const isBlocked = isBuilder && outstandingRoles.length > 0;
      const hasFailed = DSM_RECIPIENT_FAILURE_STATUSES.includes(signer.status);
      const isActionable = !isClosed && !isSigned && !isBlocked;

      return {
        signer,
        initials: signerInitials(signer.signee?.name),
        roleLabel: DSM_SIGNER_ROLE_LABELS[entityType] ?? signer.entityType,
        statusTag: this.#statusTag(signer, { isInPerson, isSigned, isBlocked, hasFailed, outstandingRoles }),
        canGetSignature: isActionable && !isBuilder && isInPerson && !hasFailed,
        canSendForSignature: isActionable && isBuilder && signer.status === DsmRecipientStatus.Pending,
        // Delivery failures can always be retried; an expired envelope can be resent to anyone.
        canResend: !isClosed && !isBuilder && (hasFailed || isExpired),
        signedDate: isSigned ? signer.lastModified : null,
        isPending: pendingSignerId === signer.id
      };
    });
  });

  #statusTag(
    signer: DsmSignOffSigner,
    state: {
      isInPerson: boolean;
      isSigned: boolean;
      isBlocked: boolean;
      hasFailed: boolean;
      outstandingRoles: string[];
    }
  ): SignerStatusTag | null {
    if (state.isSigned) return { label: 'Signed', severity: 'success' };
    if (signer.status === DsmRecipientStatus.Declined) return { label: 'Declined', severity: 'danger' };
    if (state.hasFailed) return { label: 'Email delivery failed', severity: 'danger' };

    if (state.isBlocked) {
      const roles = state.outstandingRoles.map(role => `"${role}"`).join(' and ');
      return { label: `Awaiting ${roles} signature`, severity: 'info' };
    }

    // In-person signers get an action button rather than a status chip.
    if (state.isInPerson) return null;

    // Pending means DocuSign hasn't been asked to notify this recipient yet — true for the
    // builder before "Send for Signature" is clicked. Nothing has gone out, so no chip claims it has.
    if (signer.status === DsmRecipientStatus.Pending) return null;

    return { label: 'Email sent for signature', severity: 'info' };
  }

  getSignature(row: SignerRow): void {
    if (this.#pendingSignerId()) return;

    const envelope = this.envelope();
    this.#pendingSignerId.set(row.signer.id);
    this.#signOffService
      .startInPersonSigning(envelope.dealDocumentId, row.signer.id, row.signer.signee?.name ?? '', window.location.href)
      .pipe(
        finalize(() => this.#pendingSignerId.set(null)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(session => {
        if (session?.signingUrl) window.location.href = session.signingUrl;
      });
  }

  sendForSignature(): void {
    if (this.isBuilderRequestPending()) return;

    const envelope = this.envelope();
    const request$: Observable<unknown> = envelope.includeBuilder
      ? this.#signOffService.sendToBuilder(envelope.dealDocumentId)
      : this.#signOffService.getBuilderSignature(envelope.dealDocumentId);

    this.isBuilderRequestPending.set(true);
    request$
      .pipe(
        finalize(() => this.isBuilderRequestPending.set(false)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#signOffContext.requestReload());
  }

  resendEmail(row: SignerRow): void {
    if (this.#pendingSignerId()) return;

    const envelope = this.envelope();
    this.#pendingSignerId.set(row.signer.id);
    this.#signOffService
      .resend(envelope.dealDocumentId)
      .pipe(
        finalize(() => this.#pendingSignerId.set(null)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe(() => this.#signOffContext.requestReload());
  }
}
